"use client";
import { CaretDownOutlined, CaretRightOutlined } from "@ant-design/icons";
import { Checkbox } from "antd";
import cn from "classnames";
import { Dispatch, memo, SetStateAction, useCallback, useEffect, useState } from "react";

export type TreeNodeType = {
    key: string;
    name: string;
    children?: TreeNodeType[];
};

const NODE_HEIGHT = 24;
const NODE_BUTTON_WIDTH = 24;



const getChildrenKeys = (node: TreeNodeType, withSelf: boolean): string[] => {
    let result = withSelf ? [node.key] : [];
    if (node.children) {
        for (const c of node.children) {
            result = result.concat(getChildrenKeys(c, true));
        }
    }
    return result;
};

function TreeNode({
    node,
    selectedNodeKey,
    onSelect,
    level = 0,
    checkedNodeKeys,
    setCheckedNodeKeys,
    handleCheckboxChange,
    treeDisabled,
    expandAll,
}: {
    node: TreeNodeType;
    selectedNodeKey?: string | null;
    onSelect?: null | ((nodeKey: string) => void);
    level?: number;
    checkedNodeKeys: Record<string, true | false | null | undefined>;
    setCheckedNodeKeys: Dispatch<SetStateAction<Record<string, boolean | null | undefined>>>;
    handleCheckboxChange: (nodeKey: string, state: boolean) => void;
    treeDisabled?: boolean;
    expandAll?: boolean[];
}) {
    const [isExpanded, setIsExpanded] = useState(false); // Состояние для раскрытия/сворачивания
    const isChecked = checkedNodeKeys[node.key] === true;
    const isIndeterminate = checkedNodeKeys[node.key] === null;

    useEffect(() => {
        if (expandAll !== undefined) {
            setIsExpanded(expandAll[0]);
        }
    }, [expandAll]);

    // const relatedCheckedNodeKeys = useMemo<Record<string, true | false | null | undefined>>(() => {
    //     const result: Record<string, true | false | null | undefined> = {};

    //     const interestingKeys = getChildrenKeys(node, false);
    //     // for (const [key, mark] of Object.entries(checkedNodeKeys)) {}
    //     for (const intKey of interestingKeys) {
    //         result[intKey] = checkedNodeKeys[intKey];
    //     }
    //     return result;
    // }, [checkedNodeKeys, node]);

    const handleToggle = useCallback(() => {
        setIsExpanded(!isExpanded); // Переключаем состояние раскрытия
    }, [isExpanded]);

    const handleSelect = useCallback(() => {
        if (onSelect) {
            onSelect(node.key);
        } // Выбираем ноду
    }, [node.key, onSelect]);

    // console.log("render");

    return (
        <div className="flex flex-col space-y-1">
            <div
                className="flex overflow-hidden w-full"
                style={{
                    height: NODE_HEIGHT,
                }}
            >
                <div
                    className="shrink-0 select-none text-xs flex justify-center items-center bg-tree-level rounded"
                    style={{
                        width: NODE_BUTTON_WIDTH,
                        marginRight:
                            NODE_BUTTON_WIDTH * level +
                            (node.children && node.children.length > 0 ? 0 : NODE_BUTTON_WIDTH),
                    }}
                >
                    {level + 1}
                </div>
                {node.children && node.children.length > 0 && (
                    <button
                        className="flex shrink-0 justify-center items-center cursor-pointer hover:bg-tree-hover rounded"
                        onClick={handleToggle}
                        style={{ width: NODE_BUTTON_WIDTH }}
                    >
                        {isExpanded ? (
                            <CaretDownOutlined className="text-xs" />
                        ) : (
                            <CaretRightOutlined className="text-xs" />
                        )}
                    </button>
                )}
                <div
                    className="flex shrink-0 justify-center items-center"
                    onClick={() => {
                        // setCheckedNodeKeys((v) => {
                        //     if (v.includes(node.key)) {
                        //         return v.filter((v) => v != node.key);
                        //     } else {
                        //         return [...v, node.key];
                        //     }
                        // });
                    }}
                    style={{ width: NODE_BUTTON_WIDTH }}
                >
                    <Checkbox
                        disabled={treeDisabled}
                        checked={isChecked}
                        indeterminate={isIndeterminate}
                        onChange={(e) => {
                            handleCheckboxChange(node.key, e.target.checked);
                            // e.stopPropagation();
                        }}
                    />
                </div>
                <div
                    style={{}}
                    className={cn("truncate flex-grow px-1 text-sm items-center select-none rounded", {
                        "bg-tree-active": selectedNodeKey === node.key,
                        "cursor-pointer": onSelect,
                    })}
                    onClick={handleSelect}
                >
                    {node.name}
                </div>
            </div>
            {isExpanded &&
                node.children &&
                node.children.length > 0 &&
                node.children.map((child) => (
                    <TreeNodeMemo
                        handleCheckboxChange={handleCheckboxChange}
                        checkedNodeKeys={checkedNodeKeys}
                        setCheckedNodeKeys={setCheckedNodeKeys}
                        key={child.key}
                        node={child}
                        selectedNodeKey={selectedNodeKey}
                        onSelect={onSelect}
                        level={level + 1}
                        treeDisabled={treeDisabled}
                    />
                ))}
        </div>
    );
}

const TreeNodeMemo = memo(TreeNode, (prProps, newProps) => {
    type PropName = keyof typeof prProps;
    const props = new Set<PropName>();
    for (const k of Object.keys(prProps)) {
        props.add(k as PropName);
    }
    for (const k of Object.keys(newProps)) {
        props.add(k as PropName);
    }

    // for (const propName of props) {
    //     if (prProps[propName] !== newProps[propName]) {
    //         console.log(propName);
    //     }
    // }

    for (const propName of props) {
        if (["checkedNodeKeys"].includes(propName)) {
            continue;
        }

        if (prProps[propName] !== newProps[propName]) {
            return false;
        }
    }

    // if (prProps.selectedNodeKey !== newProps.checkedNodeKeys) {

    //     console.log(newProps.selectedNodeKey, prProps.selectedNodeKey)
    //     if (newProps.selectedNodeKey === newProps.node.key) {
    //         console.log("newSelect", newProps.node.key);
    //         return false;
    //     }
    //     if (prProps.selectedNodeKey === newProps.node.key) {
    //         console.log("oldSelect", newProps.node.key);
    //         return false;
    //     }
    // }

    // if (newProps.level === 0 ?? newProps.level == undefined) {
    //     console.log("here", prProps.checkedNodeKeys !== newProps.checkedNodeKeys);
    //     if (prProps.checkedNodeKeys !== newProps.checkedNodeKeys) {
    //         return false;
    //     }
    // }
    const interestingKeys = getChildrenKeys(newProps.node, true);
    for (const intKey of interestingKeys) {
        if (newProps.checkedNodeKeys[intKey] !== prProps.checkedNodeKeys[intKey]) {
            return false;
        }
    }

    return true;
});

const findNodeByKey = (nodes: TreeNodeType[], key: string): TreeNodeType | null => {
    for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children) {
            const found = findNodeByKey(node.children, key);
            if (found) return found;
        }
    }
    return null;
};

export const calcCheckedStates = (
    tree: TreeNodeType[],
    checked: Record<string, boolean | null | undefined>
): Record<string, boolean | null | undefined> => {
    let result: Record<string, boolean | null | undefined> = {};
    for (const n of tree) {
        if (n.children) {
            result = Object.assign(result, calcCheckedStates(n.children, checked));
            let count_all = 0;
            let count_checked = 0;
            let count_partial = 0;
            for (const c of n.children) {
                count_all += 1;
                if (result[c.key] === true) {
                    count_checked += 1;
                }
                if (result[c.key] === null) {
                    count_partial += 1;
                }
            }
            if (count_all == count_checked) {
                result[n.key] = true;
            } else if (count_checked !== 0 || count_partial !== 0) {
                result[n.key] = null;
            } else {
                result[n.key] = false;
            }
        } else {
            result[n.key] = checked[n.key];
        }
    }

    return result;
};

const Tree = ({
    selectedNodeKey,
    handleSelectNode,
    checkedNodeKeys,
    setCheckedNodeKeys,
    treeData,
    disabled: treeDisabled,
    expandAll,
}: {
    treeData?: TreeNodeType[];
    selectedNodeKey?: string | null;
    handleSelectNode?: null | ((nodeKey: string) => void);
    checkedNodeKeys: Record<string, boolean | null | undefined>;
    setCheckedNodeKeys: Dispatch<SetStateAction<Record<string, boolean | null | undefined>>>;
    disabled?: boolean;
    expandAll?: boolean[];
}) => {
    const handleCheckboxChange = useCallback(
        (nodeKey: string, isChecked: boolean) => {
            if (!treeData) {
                return;
            }
            const node = findNodeByKey(treeData, nodeKey); // Находим ноду по key
            if (!node) {
                console.error(`Unable to find node with key ${nodeKey} in tree`);
                return;
            }

            const forwardUpdateSet = new Set(getChildrenKeys(node, true));
            setCheckedNodeKeys((prev) => {
                const newState: Record<string, boolean | null | undefined> = { ...prev };
                for (const updateKey of forwardUpdateSet) {
                    newState[updateKey] = isChecked;
                }

                return calcCheckedStates(treeData, newState);
            });
        },
        [setCheckedNodeKeys, treeData]
    );

    if (!treeData) {
        return <div>Нет данных</div>;
    }

    return (
        <div className="flex flex-col space-y-1">
            {treeData.map((node) => (
                <TreeNodeMemo
                    expandAll={expandAll}
                    treeDisabled={treeDisabled}
                    handleCheckboxChange={handleCheckboxChange}
                    checkedNodeKeys={checkedNodeKeys}
                    setCheckedNodeKeys={setCheckedNodeKeys}
                    key={node.key}
                    node={node}
                    selectedNodeKey={selectedNodeKey}
                    onSelect={handleSelectNode}
                    level={0}
                />
            ))}
        </div>
    );
};

export const treeCheckedArrayFromRecord = (value: Record<string, true | false | null | undefined>): string[] => {
    const checked = [];
    for (const [k, v] of Object.entries(value)) {
        if (v) {
            checked.push(k);
        }
    }
    return checked;
};

export const treeCheckedRecordFromArray = (value: string[]): Record<string, true | false | null | undefined> => {
    const result: Record<string, true | false | null | undefined> = {};
    for (const k of value) {
        result[k] = true;
    }
    return result;
};

const TreeMomo = memo(Tree);

export default TreeMomo;
